import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI, pricesAPI, walletAPI } from '../services/api';
import { useAuth } from '../auth/useAuth';

const initialForm = {
  fullName: '',
  documentType: 'passport',
  documentNumber: '',
  idFrontFile: null,
  idBackFile: null,
  addressDocType: 'bank_statement',
  addressDocFile: null,
  otherDocFiles: []
};

// Max encoded size per file so the total payload stays under Vercel's 4.5 MB body limit.
// Base64 inflates by ~33 %, so 1 MB raw ≈ 1.37 MB encoded.  Keep each file ≤ 1 MB raw.
const MAX_RAW_BYTES = 1 * 1024 * 1024; // 1 MB
const IMG_MAX_DIM   = 1400;            // longest side in pixels
const IMG_QUALITY   = 0.75;            // JPEG quality

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

async function fileToBase64(file) {
  // For images: resize + re-encode via canvas so they stay small
  if (file.type.startsWith('image/')) {
    const dataUrl = await readFileAsDataURL(file);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        // Scale down if either dimension exceeds the limit
        if (width > IMG_MAX_DIM || height > IMG_MAX_DIM) {
          if (width >= height) { height = Math.round(height * IMG_MAX_DIM / width); width = IMG_MAX_DIM; }
          else                 { width  = Math.round(width  * IMG_MAX_DIM / height); height = IMG_MAX_DIM; }
        }
        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', IMG_QUALITY));
      };
      img.onerror = () => reject(new Error(`Failed to load image: ${file.name}`));
      img.src = dataUrl;
    });
  }

  // For PDFs and other files: enforce hard size limit before encoding
  if (file.size > MAX_RAW_BYTES) {
    throw new Error(`File "${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB. Please upload a file smaller than 1 MB.`);
  }
  return readFileAsDataURL(file);
}

async function hashFile(file) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function FileUploadField({ label, hint, file, onChange, accept, required }) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6, fontSize: '0.82rem' }}>{hint}</span>}
      </label>
      <label className={`kyc-upload-area${file ? ' has-file' : ''}`}>
        <input type="file" accept={accept || 'image/*,.pdf'} style={{ display: 'none' }} onChange={(e) => onChange(e.target.files?.[0] || null)} />
        {file
          ? <span>✓ {file.name}</span>
          : <span>Click to upload or drag &amp; drop</span>}
      </label>
    </div>
  );
}

function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function RecoverWalletPage() {
  const { t } = useTranslation();
  useAuth();

  const statusCopy = {
    NO_KYC:         { title: t('recover.status.noKycTitle'),         body: t('recover.status.noKycDesc') },
    KYC_SUBMITTED:  { title: t('recover.status.submittedTitle'),     body: t('recover.status.submittedDesc') },
    KYC_PROCESSING: { title: t('recover.status.inProgressTitle'),    body: t('recover.status.inProgressDesc') },
    KYC_MORE_DOCS:  { title: t('recover.status.additionalDocsTitle'),body: t('recover.status.additionalDocsDesc') },
    KYC_APPROVED:   { title: t('recover.status.approvedTitle'),      body: t('recover.status.approvedDesc') },
    KYC_REJECTED:   { title: t('recover.status.rejectedTitle'),      body: t('recover.status.rejectedDesc') },
    SEED_READY:     { title: t('recover.status.seedReadyTitle'),     body: t('recover.status.seedReadyDesc') },
    SEED_REVEALED:  { title: t('recover.status.seedReadyTitle'),     body: t('recover.status.seedReadyDesc2') },
  };
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('NO_KYC');
  const [message, setMessage] = useState('');
  const [walletExists, setWalletExists] = useState(false);
  const [recoveryBalance, setRecoveryBalance] = useState(null);
  const livePricesRef = useRef(null); // ref to store live prices without adding to dependencies
  const shouldPollRef = useRef(true);  // set to false on 401 to stop polling after session expiry
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true); // hide UI until first loadStatus completes
  const [submitMessage, setSubmitMessage] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const [seedPayload, setSeedPayload] = useState(null);
  const [copied, setCopied] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const statusText = statusCopy[status] || statusCopy.NO_KYC;

  const needsBack = form.documentType === 'national_id' || form.documentType === 'drivers_license';
  const canSubmitKyc = !loading &&
    form.fullName.trim() &&
    form.documentNumber.trim() &&
    form.idFrontFile &&
    (!needsBack || form.idBackFile) &&
    form.addressDocFile;

  const showKycForm = status === 'NO_KYC' || status === 'KYC_MORE_DOCS' || status === 'KYC_REJECTED';
  const showSubmitted = status === 'KYC_SUBMITTED';
  const showProcessing = status === 'KYC_PROCESSING';
  const showApproved = status === 'KYC_APPROVED';
  const showRejected = status === 'KYC_REJECTED';
  const showSeedReady = status === 'SEED_READY';
  const showSeedRevealed = status === 'SEED_REVEALED';

  const loadStatus = useCallback(async () => {
    if (!shouldPollRef.current) return;

    // ── Step 1: fetch recovery status — this drives the UI view ──────────────
    // Keep status/wallet state completely isolated from the balance fetch so
    // a failing balance request can never wipe out a valid SEED_READY status.
    let statusValue = 'NO_KYC';
    let walletReady = false;
    try {
      const response = await walletAPI.getRecoveryStatus();
      statusValue = response.data?.status || 'NO_KYC';
      walletReady = Boolean(response.data?.walletExists);
      setStatus(statusValue);
      setMessage(response.data?.message || '');
      setWalletExists(walletReady);
    } catch (error) {
      // 401 means session expired — stop polling and let the auth interceptor handle logout
      if (error?.response?.status === 401) {
        shouldPollRef.current = false;
        return;
      }
      // 404 is expected when recovery wallet doesn't exist yet
      if (error?.response?.status !== 404) {
        console.error('Error loading recovery status:', error);
      }
      setStatus('NO_KYC');
      setWalletExists(false);
      setRecoveryBalance(null);
      return;
    }

    // ── Step 2: fetch balance (best-effort, never affects status) ────────────
    if (!walletReady) {
      setRecoveryBalance(null);
      return;
    }
    try {
      const walletRes = await walletAPI.getRecoveryWallet();
      const wallet = walletRes.data;
      if (wallet?.address && wallet?.network && !wallet.address.startsWith('custom:')) {
        const balanceRes = await walletAPI.getBalance(wallet.address, wallet.network);
        const amount = toNumber(
          balanceRes.data?.native?.balance
          ?? balanceRes.data?.balance
          ?? balanceRes.data?.totalBtc
        );
        const symbol = wallet.network === 'bitcoin' ? 'BTC' : wallet.network.toUpperCase();
        const prices = livePricesRef.current;
        const rate = symbol === 'BTC'
          ? prices?.bitcoin?.usd
          : symbol === 'ETH'
            ? prices?.ethereum?.usd
            : symbol === 'USDT'
              ? prices?.tether?.usd
              : null;
        const usd = typeof rate === 'number' && typeof amount === 'number' ? amount * rate : null;
        setRecoveryBalance({ amount, symbol, usd });
      }
    } catch (balanceErr) {
      // balance fetch failing is non-critical — do not change status or wallet state
      console.warn('Balance fetch failed (non-critical):', balanceErr?.message);
    }
  }, []); // stable — reads livePricesRef.current instead of livePrices state

  const loadPrices = useCallback(async () => {
    try {
      const response = await pricesAPI.getLivePrices();
      livePricesRef.current = response.data;
    } catch (error) {
      livePricesRef.current = null;
    }
  }, []); // stable — no state dependencies

  useEffect(() => {
    const fetchNow = async () => {
      await loadPrices();
      await loadStatus();
      setInitializing(false);
    };

    fetchNow();

    const statusInterval = setInterval(loadStatus, 30000);
    const priceInterval = setInterval(loadPrices, 60000);

    return () => {
      shouldPollRef.current = false;
      clearInterval(statusInterval);
      clearInterval(priceInterval);
    };
  }, [loadPrices, loadStatus]);

  const onSubmitKyc = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSubmitMessage('');
    setUploadProgress('Preparing documents...');

    try {
      try { await authAPI.fetchCsrfToken(); } catch (_) { /* non-fatal */ }

      // Convert files to base64 — no external storage needed
      setUploadProgress('Reading ID document...');
      const idFrontUrl = await fileToBase64(form.idFrontFile);

      let idBackUrl = '';
      if (needsBack && form.idBackFile) {
        setUploadProgress('Reading ID back...');
        idBackUrl = await fileToBase64(form.idBackFile);
      }

      setUploadProgress('Reading address document...');
      const addressDocUrl = await fileToBase64(form.addressDocFile);

      const otherDocUrls = [];
      for (let i = 0; i < form.otherDocFiles.length; i++) {
        setUploadProgress(`Reading additional document ${i + 1}...`);
        const b64 = await fileToBase64(form.otherDocFiles[i]);
        if (b64) otherDocUrls.push(b64);
      }

      const documentHash = await hashFile(form.idFrontFile);

      setUploadProgress('Submitting...');
      await walletAPI.submitKyc({
        fullName: form.fullName,
        documentType: form.documentType,
        documentNumber: form.documentNumber,
        documentHash,
        idFrontUrl,
        idBackUrl,
        addressDocType: form.addressDocType,
        addressDocUrl,
        otherDocUrls
      });

      setSubmitMessage(t('recover.progress.submitted'));
      setForm(initialForm);
      await loadStatus();
    } catch (error) {
      setSubmitMessage(error?.response?.data?.message || error?.message || 'Unable to submit KYC.');
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const onRevealSeed = async () => {
    setRevealLoading(true);
    setSeedPayload(null);

    try {
      const response = await walletAPI.getSeed();
      setSeedPayload(response.data);
      await loadStatus();
    } catch (error) {
      setSubmitMessage(error?.response?.data?.message || 'Unable to reveal seed phrase.');
    } finally {
      setRevealLoading(false);
    }
  };

  const showSeedButton = showSeedReady || showSeedRevealed || (showApproved && walletExists);

  const navigate = useNavigate();

  return (
    <div className="rw-theme rw-page rw-recover">
      <div className="rw-recover-container">
        <button
          onClick={() => navigate('/dashboard')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '1rem', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}
        >
          {t('recover.backToDashboard')}
        </button>
        <div className="rw-recover-header">
          <h1>{t('recover.title')}</h1>
          <p className="rw-muted">{t('recover.kycRequired')}</p>
        </div>

        {initializing ? (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
            <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto 12px' }} />
            {t('recover.loading')}
          </div>
        ) : (<>
          {showKycForm && (
          <div className="rw-recover-box">
            <p><strong>{statusText.title}</strong></p>
            <p>{statusText.body}</p>
            <form onSubmit={onSubmitKyc} className="rw-recover-form">

              {/* ── Section 1: Identity Document ── */}
              <div style={{ margin: '1.5rem 0 0.5rem', fontWeight: 700, fontSize: '1rem', color: 'var(--primary)' }}>
                {t('recover.form.step1')}
              </div>
              <div className="form-group">
                <label className="form-label">{t('recover.form.fullName')} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input
                  className="form-input"
                  type="text"
                  placeholder={t('recover.form.fullNamePlaceholder')}
                  value={form.fullName}
                  onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('recover.form.docType')} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <select
                  className="form-input form-select"
                  value={form.documentType}
                  onChange={(e) => setForm((p) => ({ ...p, documentType: e.target.value, idBackFile: null }))}
                >
                  <option value="passport">{t('recover.form.passport')}</option>
                  <option value="national_id">{t('recover.form.nationalId')}</option>
                  <option value="drivers_license">{t('recover.form.driversLicense')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('recover.form.docNumber')} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input
                  className="form-input"
                  type="text"
                  placeholder={t('recover.form.docNumberPlaceholder')}
                  value={form.documentNumber}
                  onChange={(e) => setForm((p) => ({ ...p, documentNumber: e.target.value }))}
                  required
                />
              </div>
              <FileUploadField
                label={t('recover.form.frontDoc')}
                hint={form.documentType === 'passport' ? t('recover.form.frontDocNote') : '(front side)'}
                file={form.idFrontFile}
                onChange={(f) => setForm((p) => ({ ...p, idFrontFile: f }))}
                required
              />
              {needsBack && (
                <FileUploadField
                  label={t('recover.form.backDoc')}
                  hint={t('recover.form.backDocNote')}
                  file={form.idBackFile}
                  onChange={(f) => setForm((p) => ({ ...p, idBackFile: f }))}
                  required
                />
              )}

              {/* ── Section 2: Address Verification ── */}
              <div style={{ margin: '1.5rem 0 0.5rem', fontWeight: 700, fontSize: '1rem', color: 'var(--primary)' }}>
                {t('recover.form.step2')}
              </div>
              <div className="form-group">
                <label className="form-label">{t('recover.form.docType')} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <select
                  className="form-input form-select"
                  value={form.addressDocType}
                  onChange={(e) => setForm((p) => ({ ...p, addressDocType: e.target.value }))}
                >
                  <option value="bank_statement">{t('recover.form.bankStatement')}</option>
                  <option value="utility_bill">{t('recover.form.utilityBill')}</option>
                </select>
              </div>
              <FileUploadField
                label={t('recover.form.addressDoc')}
                hint={t('recover.form.addressDocNote')}
                file={form.addressDocFile}
                onChange={(f) => setForm((p) => ({ ...p, addressDocFile: f }))}
                required
              />

              {/* ── Section 3: Other Documents (optional) ── */}
              <div style={{ margin: '1.5rem 0 0.5rem', fontWeight: 700, fontSize: '1rem', color: 'var(--primary)' }}>
                {t('recover.form.step3')} <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(optional)</span>
              </div>
              <div className="form-group">
                <label className="form-label">{t('recover.form.uploadSupporting')}</label>
                <label className="kyc-upload-area">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => setForm((p) => ({ ...p, otherDocFiles: Array.from(e.target.files || []) }))}
                  />
                  {form.otherDocFiles.length > 0
                    ? <span>{t('recover.form.filesSelected', { count: form.otherDocFiles.length })}</span>
                    : <span>{t('recover.form.uploadHint')}</span>}
                </label>
              </div>

              {uploadProgress && (
                <div style={{ padding: '8px 12px', background: 'rgba(102,126,234,0.1)', borderRadius: 8, fontSize: '0.9rem', color: 'var(--primary)', fontWeight: 600 }}>
                  {uploadProgress}
                </div>
              )}

              <button className="rw-btn rw-btn-primary" type="submit" disabled={!canSubmitKyc} style={{ marginTop: '1rem' }}>
                {loading ? t('recover.progress.submitting') : t('recover.form.submit')}
              </button>
              {submitMessage && <div className="rw-admin-message" style={{ marginTop: 12 }}>{submitMessage}</div>}
            </form>
          </div>
        )}

        {showSubmitted && (
          <div className="rw-recover-box">
            <p><strong>{statusText.title}</strong></p>
            <p>{statusText.body}</p>
          </div>
        )}

        {showProcessing && (
          <div className="rw-recover-loader">Verifying identity… please wait</div>
        )}

        {status === 'KYC_MORE_DOCS' && (
          <div className="rw-recover-warning">
            Additional documents required:<br /><br />
            <em>“{message || 'Please submit the requested documents.'}”</em>
          </div>
        )}

        {showRejected && (
          <div className="rw-recover-error">
            Verification failed. {message || 'Please upload another ID and resubmit.'}
          </div>
        )}

        {showApproved && (
          <div className="rw-recover-success">Identity verified successfully.</div>
        )}

        {showSeedButton && (
          <div className="rw-recover-box">
            <p><strong>{t('recover.status.seedReadyTitle')}</strong></p>
            <p>{t('recover.status.seedReadyDesc2')} {t('recover.seedWarning')}</p>
            <div style={{
              background: 'rgba(255, 170, 0, 0.1)',
              border: '1px solid rgba(255, 170, 0, 0.4)',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: '0.87rem',
              color: '#ffc107',
              marginBottom: 14,
              lineHeight: 1.6
            }}>
              ⚠️ Make sure you are in a private location. Have pen and paper ready to write down the 12 words before clicking reveal.
            </div>
            {recoveryBalance && (
              <div className="rw-recover-frozen">
                Frozen balance: {typeof recoveryBalance.amount === 'number'
                  ? `${recoveryBalance.amount.toFixed(6)} ${recoveryBalance.symbol}`
                  : '—'}
                {typeof recoveryBalance.usd === 'number' && (
                  <span className="rw-recover-frozen-usd">({recoveryBalance.usd.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})</span>
                )}
              </div>
            )}
            <button className="rw-btn rw-btn-primary" onClick={onRevealSeed} disabled={revealLoading}>
              {revealLoading ? t('recover.decrypting') : seedPayload ? t('recover.refreshSeed') : t('recover.form.revealSeed')}
            </button>
          </div>
        )}

        {revealLoading && (
          <div className="rw-recover-loader">
            <img src="/images/recovery-cyber.svg" alt="system processing" />
          </div>
        )}

        {showSeedRevealed && !seedPayload && (
          <div className="rw-recover-box">
            <p><strong>{t('recover.status.seedReadyTitle')}</strong></p>
            <p>{t('recover.status.seedReadyDesc2')}</p>
          </div>
        )}

        {seedPayload && (
          <div className="rw-recover-seed">
            <div className="rw-recover-seed-warning">
              ⚠️ <strong>Save these 12 words immediately.</strong> Write them down on paper and store them offline in a safe location. This phrase <strong>will NOT be shown again</strong> — it is the only way to recover your wallet.
            </div>

            {/* Seed grid — always visible once revealed */}
            <div
              style={{
                position: 'relative',
                borderRadius: 12,
                overflow: 'hidden',
                marginBottom: 12,
              }}
            >
              <div className="rw-recover-seed-grid">
                {seedPayload.mnemonic.trim().split(/\s+/).map((word, i) => (
                  <div key={i} className="rw-recover-seed-word">
                    <span className="rw-recover-seed-num">{i + 1}</span>
                    <span className="rw-recover-seed-word-text">{word}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Copy button */}
            <button
              className="rw-btn rw-btn-secondary"
              style={{ width: '100%' }}
              onClick={() => {
                navigator.clipboard.writeText(seedPayload.mnemonic).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 3000);
                }).catch(() => {});
              }}
            >
              {copied ? '✅ Copied!' : '📋 Copy All 12 Words'}
            </button>

            <div className="rw-recover-seed-tip">
              💡 Do not store this phrase in email, cloud storage, or screenshots. Write it on paper and keep it in a secure location like a safe.
            </div>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}

export default RecoverWalletPage;
